import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type {
  RuntimeWallet,
  RuntimeWalletSort,
  RuntimeWalletsSummary,
} from "../types/payments-wallets.types";
import {
  DEFAULT_OVERVIEW_SORT,
  hiddenUnfundedWallets,
  readWalletsOverviewUrl,
  type WalletsOverviewFilters,
} from "../utils/walletsOverview";

export type { WalletsOverviewFilters } from "../utils/walletsOverview";

export const WALLETS_OVERVIEW_PAGE_SIZE = 25;

const SEARCH_DEBOUNCE_MS = 350;

/** The balance sorts. Without a currency the backend ranks them within each currency. */
export const BALANCE_SORTS: ReadonlySet<RuntimeWalletSort> = new Set([
  "held_desc",
  "held_asc",
  "available_desc",
  "pending_desc",
]);

export const DEFAULT_WALLETS_OVERVIEW_FILTERS: WalletsOverviewFilters = {
  search: "",
  currency: null,
  status: null,
  withBalance: true,
  includeColoca: false,
  minHeld: "",
  maxHeld: "",
  sort: DEFAULT_OVERVIEW_SORT,
};

export interface UseWalletsOverviewResult {
  filters: WalletsOverviewFilters;
  /** Changes some filters and goes back to the first page. Clearing the currency clears the range too. */
  setFilters: (patch: Partial<WalletsOverviewFilters>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  page: number;
  setPage: (page: number) => void;
  rows: RuntimeWallet[];
  total: number;
  isLoadingRows: boolean;
  rowsError: string | null;
  summary: RuntimeWalletsSummary | null;
  isLoadingSummary: boolean;
  summaryError: string | null;
  /**
   * Wallets the filters match that "with a balance only" leaves out. Zero until the summary for these exact
   * filters has answered, so a stale count never offers wallets that are not there.
   */
  hiddenWithoutBalance: number;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

/**
 * Staff overview of every client's payment wallets: balances summed per currency (`GET
 * /internal/payments/wallets/summary`) and the wallets themselves, filtered and ranked server-side (`GET
 * /internal/payments/wallets`). Filters live in the URL, read once on mount.
 *
 * The summary only follows the filters that change the population it sums (search, status, Coloca); the list
 * follows all of them. Each read is last-wins on its own, so a slow answer never replaces a newer one.
 * `reloadSignal` re-reads both when the host bumps it.
 */
export function useWalletsOverview(reloadSignal = 0): UseWalletsOverviewResult {
  const { user } = useAuth();
  const principalKey = user?.id ?? "anon";

  const [searchParams] = useSearchParams();
  const [initial] = useState(() => readWalletsOverviewUrl(searchParams));
  const [filters, setFiltersState] = useState<WalletsOverviewFilters>(initial.filters);
  const [page, setPageState] = useState(initial.page);

  useUrlParamMirror("q", filters.search);
  useUrlParamMirror("currency", filters.currency);
  useUrlParamMirror("status", filters.status);
  useUrlParamMirror("funded", filters.withBalance ? null : "0");
  useUrlParamMirror("coloca", filters.includeColoca ? "1" : null);
  useUrlParamMirror("min", filters.minHeld);
  useUrlParamMirror("max", filters.maxHeld);
  useUrlParamMirror("sort", filters.sort === DEFAULT_WALLETS_OVERVIEW_FILTERS.sort ? null : filters.sort);
  useUrlParamMirror("page", page > 1 ? String(page) : null);

  const search = useDebouncedValue(filters.search.trim(), SEARCH_DEBOUNCE_MS);
  const minHeld = useDebouncedValue(filters.minHeld, SEARCH_DEBOUNCE_MS);
  const maxHeld = useDebouncedValue(filters.maxHeld, SEARCH_DEBOUNCE_MS);

  const [hasAccess, setHasAccess] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const currency = filters.currency ?? undefined;
  const rowsQuery = useMemo(
    () => ({
      search,
      currency,
      status: filters.status ?? undefined,
      withBalance: filters.withBalance,
      excludeRoot: !filters.includeColoca,
      minHeld: currency ? minHeld : undefined,
      maxHeld: currency ? maxHeld : undefined,
      sort: filters.sort,
      skip: (page - 1) * WALLETS_OVERVIEW_PAGE_SIZE,
      limit: WALLETS_OVERVIEW_PAGE_SIZE,
    }),
    [
      currency,
      filters.includeColoca,
      filters.sort,
      filters.status,
      filters.withBalance,
      maxHeld,
      minHeld,
      page,
      search,
    ],
  );
  const summaryQuery = useMemo(
    () => ({ search, status: filters.status ?? undefined, excludeRoot: !filters.includeColoca }),
    [filters.includeColoca, filters.status, search],
  );
  // Loading is derived, never set from the effect: a read is loading until an answer for exactly this query
  // (and this reload) has settled.
  const rowsKey = `${principalKey}|${reloadKey}|${reloadSignal}|${JSON.stringify(rowsQuery)}`;
  const summaryKey = `${principalKey}|${reloadKey}|${reloadSignal}|${JSON.stringify(summaryQuery)}`;

  const [rowsState, setRowsState] = useState<{
    key: string | null;
    rows: RuntimeWallet[];
    total: number;
    error: string | null;
  }>({ key: null, rows: [], total: 0, error: null });
  const [summaryState, setSummaryState] = useState<{
    key: string | null;
    summary: RuntimeWalletsSummary | null;
    error: string | null;
  }>({ key: null, summary: null, error: null });
  const rowsSeq = useRef(0);
  const summarySeq = useRef(0);

  const deny = useCallback((err: unknown): boolean => {
    if (!HttpApiService.isAccessDeniedError(err)) return false;
    setHasAccess(false);
    return true;
  }, []);

  useEffect(() => {
    if (!hasAccess) return;
    const seq = ++summarySeq.current;
    PaymentsWalletsService.summary(summaryQuery)
      .then((res) => {
        if (seq === summarySeq.current) setSummaryState({ key: summaryKey, summary: res.data, error: null });
      })
      .catch((err: unknown) => {
        if (seq !== summarySeq.current || deny(err)) return;
        setSummaryState((current) => ({
          key: summaryKey,
          summary: current.summary,
          error: HttpApiService.getErrorMessage(err),
        }));
      });
  }, [deny, hasAccess, summaryKey, summaryQuery]);

  useEffect(() => {
    if (!hasAccess) return;
    const seq = ++rowsSeq.current;
    PaymentsWalletsService.list(rowsQuery)
      .then((res) => {
        if (seq !== rowsSeq.current) return;
        setRowsState({ key: rowsKey, rows: res.data.data, total: res.data.total, error: null });
      })
      .catch((err: unknown) => {
        if (seq !== rowsSeq.current || deny(err)) return;
        setRowsState({ key: rowsKey, rows: [], total: 0, error: HttpApiService.getErrorMessage(err) });
      });
  }, [deny, hasAccess, rowsKey, rowsQuery]);

  const setFilters = useCallback((patch: Partial<WalletsOverviewFilters>) => {
    setFiltersState((current) => {
      const next = { ...current, ...patch };
      if (!next.currency) {
        next.minHeld = "";
        next.maxHeld = "";
      }
      return next;
    });
    setPageState(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_WALLETS_OVERVIEW_FILTERS);
    setPageState(1);
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      (Object.keys(DEFAULT_WALLETS_OVERVIEW_FILTERS) as (keyof WalletsOverviewFilters)[]).some(
        (key) => filters[key] !== DEFAULT_WALLETS_OVERVIEW_FILTERS[key],
      ),
    [filters],
  );

  const summaryIsCurrent = summaryState.key === summaryKey && summaryState.error === null;
  const hiddenWithoutBalance = useMemo(
    () =>
      summaryIsCurrent && summaryState.summary
        ? hiddenUnfundedWallets(summaryState.summary.currencies, filters)
        : 0,
    [filters, summaryIsCurrent, summaryState.summary],
  );

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    filters,
    setFilters,
    resetFilters,
    hasActiveFilters,
    page,
    setPage: setPageState,
    rows: rowsState.rows,
    total: rowsState.total,
    isLoadingRows: hasAccess && rowsState.key !== rowsKey,
    rowsError: rowsState.error,
    summary: summaryState.summary,
    isLoadingSummary: hasAccess && summaryState.key !== summaryKey,
    summaryError: summaryState.error,
    hiddenWithoutBalance,
    hasAccess,
    refresh,
  };
}
